import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { AppService } from './core/app.service';
import { ConfirmHostComponent } from './shared/confirm/confirm-host.component';
import { SelectionHoldHostComponent } from './shared/selection-hold/selection-hold-host.component';
import { ToolboxComponent } from './shared/toolbox/toolbox.component';
import { VoyagerToastHostComponent } from './shared/toast/voyager-toast-host.component';
import { AppShellComponent } from './shared/app-shell/app-shell.component';
import { TweakRadioComponent, TweakSectionComponent, TweaksPanelComponent } from './shared/tweaks/tweaks-panel.component';
import { IconComponent } from './shared/icon/icon.component';

interface RouteMeta {
  layout?: string;
  crumbs?: string[];
}

const EMPTY_META: RouteMeta = {};

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AppShellComponent,
    VoyagerToastHostComponent,
    ConfirmHostComponent,
    SelectionHoldHostComponent,
    ToolboxComponent,
    TweaksPanelComponent,
    TweakSectionComponent,
    TweakRadioComponent,
    IconComponent,
  ],
  templateUrl: './app.html',
})
export class App {
  readonly app = inject(AppService);
  private readonly router = inject(Router);
  private readonly activated = inject(ActivatedRoute);

  private readonly routeData = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.deepestRouteData()),
      startWith(EMPTY_META),
    ),
    { initialValue: EMPTY_META },
  );

  readonly isBlankLayout = computed(() => this.routeData().layout === 'blank');
  readonly crumbs = computed(() => this.routeData().crumbs ?? []);

  nav(route: string): void {
    this.app.navigate(route);
  }

  /** Read a tweak-bag value as a string, falling back to a default. */
  tweakStr(key: string, dflt: string): string {
    const v = this.app.tweaks()[key];
    return typeof v === 'string' ? v : dflt;
  }

  private deepestRouteData(): RouteMeta {
    let r = this.activated;
    while (r.firstChild) r = r.firstChild;
    return (r.snapshot.data as RouteMeta) ?? EMPTY_META;
  }
}
